"use client"

import Image from "next/image"
import { renderHeaderConditionally } from "../../utils/functions"
import { TerraLink } from "../../component/TerraLink/TerraLink"
import { CommonDescription } from "../../component/CommonDescription/CommonDescription"
import { colorSettingDetection } from "../../utils/functions"
import { darkTextureWrapper } from "../../utils/functions"
import { formatFocusArea } from "../../utils/formatFocusArea"
import {
  layoutHeaderClasses,
  layoutSubheaderClasses,
  layoutEyebrowClasses,
} from "@/components/tailwindHelpers"

export interface ResourceItem {
  image?: {
    src: string
    alt: string
    width?: number
    height?: number
    focusArea?: string
  }
  header: string
  description: string
  link: {
    url: string
    text: string
    ariaLabel?: string
  }
}

export interface LayoutFeaturedResources {
  eyebrow?: string
  header?: string
  subheader?: string
  description?: string
  featuredResourceItems?: Array<ResourceItem>
  ctas?: [
    {
      url: string
      text: string
      ariaLabel?: string
    },
  ]
  headerElement?: "h1" | "h2"
  isHeaderBig?: boolean
  presetTheme?:
    | "t2-theme-one"
    | "t2-theme-two"
    | "t2-theme-three"
    | "t2-theme-four"
    | "t2-theme-five"
    | "t2-theme-six"
    | "t2-theme-seven"
  componentImagePriority?: "high" | "auto" | "low"
  isLCP?: boolean
}

const HEADER_NORMAL = "font-light text-4xl leading-[1.2]"
const HEADER_BIG =
  "text-gray-m9 text-7xl font-extrabold leading-[96px] tracking-tighter max-sm:text-black max-sm:text-5xl max-sm:leading-[3.25rem] max-sm:tracking-tighter"

const DARK_LINK_OVERRIDES =
  "t2-dark:[&_.t2-terra-text-link]:text-yellow-m10 t2-dark:[&_.t2-terra-text-link:hover]:text-white t2-dark:[&_.t2-terra-text-link:active]:text-white t2-dark:[&_.t2-terra-text-link:focus]:text-white"

const ITEM_LINK =
  "[&_.t2-terra-text-link]:text-blue-trmb [&_.t2-terra-text-link]:font-semibold [&_.t2-terra-text-link]:no-underline [&_.t2-terra-text-link]:items-center [&_.t2-terra-text-link:hover]:text-gray-m9 [&_.t2-icon]:ml-4"

export const LayoutFeaturedResources = ({
  eyebrow,
  header,
  headerElement = "h2",
  subheader,
  description,
  featuredResourceItems,
  ctas,
  isHeaderBig,
  presetTheme,
  componentImagePriority,
  isLCP = false,
}: LayoutFeaturedResources) => {
  const colorSetting = colorSettingDetection(presetTheme || "")
  const headerClasses = `${layoutHeaderClasses} mb-0 ${isHeaderBig ? HEADER_BIG : HEADER_NORMAL}`

  const finalHeader = renderHeaderConditionally(
    headerClasses,
    header || "",
    headerElement,
  )

  return (
    <div
      className={`${presetTheme || ""} ${colorSetting} ${DARK_LINK_OVERRIDES}`}
      style={darkTextureWrapper(presetTheme || "")}
      data-testid="layout-featured-resources"
    >
      <div className="w-full px-5 lg:px-20 m-auto max-w-[1440px] py-[100px]">
        {(finalHeader || eyebrow || subheader || description) && (
          <div className="py-20 px-0 min-lg:px-2.5 basis-full max-w-full ml-0 min-lg:basis-2/3 min-lg:max-w-[66.666%]">
            {eyebrow && <div className={layoutEyebrowClasses}>{eyebrow}</div>}
            {finalHeader}
            {subheader && (
              <div
                className={`${layoutSubheaderClasses} ${header ? "mt-[50px]" : ""}`}
              >
                {subheader}
              </div>
            )}
            {description && (
              <CommonDescription
                description={description}
                className="t2-layout-featured-resources__description t2-layout__description"
              />
            )}
          </div>
        )}

        {featuredResourceItems && (
          <div className="w-full px-5 lg:px-20 m-auto max-w-[1440px]  max-lg:!px-0 ">
            <div className="w-full flex items-start flex items-start flex-wrap gap-x-5">
              {featuredResourceItems?.map((item, index) => {
                const focusParam = item?.image?.focusArea
                  ? formatFocusArea(item.image.focusArea)
                  : null
                const imageSrc =
                  (item?.image?.src || "") +
                  (focusParam
                    ? item?.image?.src?.includes("?")
                      ? `&f=${focusParam}`
                      : `?f=${focusParam}`
                    : "")
                return (
                  <div
                    className="flex no-underline mb-[50px] last:mb-0 min-sm:w-[calc(50%-20px)]  min-sm:last:mb-[50px] max-sm:flex-col "
                    key={index}
                    data-testid="featured-resource-item"
                  >
                    <div className="min-w-[88px] max-sm:max-w-[88px]">
                      <Image
                        className="w-full h-auto rounded-lg block"
                        src={imageSrc}
                        alt={item?.image?.alt || ""}
                        width={item?.image?.width || 88}
                        height={item?.image?.height || 88}
                        priority={isLCP}
                        loading={isLCP ? undefined : "lazy"}
                      />
                    </div>
                    <div className="w-auto pl-5 max-sm:pl-0 max-sm:pt-[15px] min-lg:pl-10">
                      <h3 className="text-base font-bold leading-snug mb-2.5">
                        {item?.header || ""}
                      </h3>
                      <div className="text-base font-light leading-snug font-[Open_Sans,sans-serif] w-full overflow-hidden text-ellipsis">
                        {item?.description}
                      </div>
                      {item?.link?.url && (
                        <div className={`mt-4 ${ITEM_LINK}`}>
                          <TerraLink
                            destination={item?.link?.url}
                            label={item?.link?.text}
                            linkType="text"
                            isIcon={true}
                            ariaLabel={item?.link?.ariaLabel}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        )}
        {ctas && ctas?.length > 0 && (
          <div className="w-full px-5 lg:px-20 m-auto max-w-[1440px] flex justify-center !px-0 py-20 max-sm:py-[60px] max-sm:flex-wrap">
            {ctas?.map((cta, index) => (
              <TerraLink
                key={index}
                destination={cta?.url}
                label={cta?.text}
                className="border border-yellow-m10 bg-yellow-m10 mx-2.5 max-sm:w-full max-sm:mb-5 visited:bg-yellow-m10"
                primary={true}
                ariaLabel={cta?.ariaLabel}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
